import React, { useState, useRef, useEffect } from 'react';
import { X, Monitor, Smartphone, Move, ZoomIn, Check } from 'lucide-react';

interface DeviceSettings {
    positionX: number;
    positionY: number;
    zoom: number;
}

interface BackgroundSettings {
    desktop: DeviceSettings;
    mobile: DeviceSettings;
}

interface BackgroundPreviewModalProps {
    isOpen: boolean;
    imageUrl: string;
    initialSettings?: BackgroundSettings;
    onApply: (settings: BackgroundSettings) => void;
    onClose: () => void;
}

const defaultSettings: BackgroundSettings = {
    desktop: { positionX: 50, positionY: 50, zoom: 100 },
    mobile: { positionX: 50, positionY: 50, zoom: 100 }
};

const BackgroundPreviewModal: React.FC<BackgroundPreviewModalProps> = ({
    isOpen,
    imageUrl,
    initialSettings,
    onApply,
    onClose
}) => {
    const [settings, setSettings] = useState<BackgroundSettings>(initialSettings || defaultSettings);
    const [activeDevice, setActiveDevice] = useState<'desktop' | 'mobile'>('desktop');
    const [isDragging, setIsDragging] = useState(false);
    const previewRef = useRef<HTMLDivElement>(null);

    // Mobile detection
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (initialSettings) {
            setSettings(initialSettings);
        }
    }, [initialSettings]);

    if (!isOpen) return null;

    const currentSettings = settings[activeDevice];

    const updatePosition = (clientX: number, clientY: number) => {
        if (!previewRef.current) return;

        const rect = previewRef.current.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) * 100;
        const y = ((clientY - rect.top) / rect.height) * 100;

        setSettings(prev => ({
            ...prev,
            [activeDevice]: {
                ...prev[activeDevice],
                positionX: Math.max(0, Math.min(100, x)),
                positionY: Math.max(0, Math.min(100, y))
            }
        }));
    };

    // Mouse handlers
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        updatePosition(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Touch handlers for mobile
    const handleTouchStart = (e: React.TouchEvent) => {
        e.preventDefault();
        setIsDragging(true);
        if (e.touches.length > 0) {
            updatePosition(e.touches[0].clientX, e.touches[0].clientY);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || e.touches.length === 0) return;
        updatePosition(e.touches[0].clientX, e.touches[0].clientY);
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const handleZoomChange = (zoom: number) => {
        setSettings(prev => ({
            ...prev,
            [activeDevice]: {
                ...prev[activeDevice],
                zoom
            }
        }));
    };

    const handleApply = () => {
        onApply(settings);
        onClose();
    };

    // Preview style that matches the actual chat rendering
    // Mobile: uses cover + scale (like in App.tsx)
    // Desktop: uses percentage for more control
    const getPreviewStyle = () => {
        const baseStyle = {
            backgroundImage: `url(${imageUrl})`,
            backgroundPosition: `${currentSettings.positionX}% ${currentSettings.positionY}%`,
            backgroundRepeat: 'no-repeat'
        };

        if (activeDevice === 'mobile') {
            // Mobile uses cover + scale, matching App.tsx behavior
            return {
                ...baseStyle,
                backgroundSize: 'cover'
            };
        } else {
            // Desktop uses percentage
            return {
                ...baseStyle,
                backgroundSize: `${currentSettings.zoom}%`
            };
        }
    };

    // Get scale transform for mobile preview (to show zoom effect)
    const getMobileTransform = () => {
        if (activeDevice === 'mobile' && currentSettings.zoom !== 100) {
            return {
                transform: `scale(${currentSettings.zoom / 100})`,
                transformOrigin: `${currentSettings.positionX}% ${currentSettings.positionY}%`
            };
        }
        return {};
    };

    const previewStyle = getPreviewStyle();

    // Responsive preview dimensions
    const getPreviewDimensions = () => {
        if (isMobile) {
            // On mobile screens, use smaller fixed sizes
            return activeDevice === 'desktop'
                ? { width: '280px', height: '158px' }  // 16:9 ratio
                : { width: '140px', height: '250px' }; // Phone ratio
        }
        // Desktop view
        return activeDevice === 'desktop'
            ? { width: '400px', height: '225px' }
            : { width: '180px', height: '320px' };
    };

    const previewDimensions = getPreviewDimensions();

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: isMobile ? '12px' : '20px'
            }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: '#1a1a2e',
                    borderRadius: isMobile ? '16px' : '24px',
                    width: '100%',
                    maxWidth: isMobile ? '100%' : '900px',
                    maxHeight: '95vh',
                    overflow: 'auto',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: isMobile ? '16px' : '20px 24px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h2 style={{
                        fontSize: isMobile ? '16px' : '20px',
                        fontWeight: '700',
                        color: 'white',
                        margin: 0
                    }}>
                        {isMobile ? 'Adjust Position' : 'Adjust Background Position'}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Device Toggle */}
                <div style={{
                    padding: isMobile ? '12px 16px' : '16px 24px',
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center'
                }}>
                    <button
                        onClick={() => setActiveDevice('desktop')}
                        style={{
                            padding: isMobile ? '8px 16px' : '10px 24px',
                            borderRadius: '12px',
                            border: 'none',
                            background: activeDevice === 'desktop'
                                ? 'linear-gradient(135deg, #a855f7, #ec4899)'
                                : 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: isMobile ? '13px' : '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <Monitor size={isMobile ? 16 : 18} />
                        Desktop
                    </button>
                    <button
                        onClick={() => setActiveDevice('mobile')}
                        style={{
                            padding: isMobile ? '8px 16px' : '10px 24px',
                            borderRadius: '12px',
                            border: 'none',
                            background: activeDevice === 'mobile'
                                ? 'linear-gradient(135deg, #a855f7, #ec4899)'
                                : 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: isMobile ? '13px' : '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <Smartphone size={isMobile ? 16 : 18} />
                        Mobile
                    </button>
                </div>

                {/* Preview Area */}
                <div style={{ padding: isMobile ? '0 16px 16px' : '0 24px 24px' }}>
                    <div style={{
                        display: 'flex',
                        gap: '24px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        {/* Active Preview (Editable) */}
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                fontSize: '12px',
                                color: 'rgba(255,255,255,0.6)',
                                marginBottom: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                            }}>
                                <Move size={14} />
                                {isMobile ? 'Tap & drag' : 'Click and drag to position'}
                            </div>
                            <div
                                ref={previewRef}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                style={{
                                    width: previewDimensions.width,
                                    height: previewDimensions.height,
                                    borderRadius: '12px',
                                    border: '3px solid #a855f7',
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    touchAction: 'none', // Prevent scroll while dragging
                                    backgroundColor: '#000'
                                }}
                            >
                                {/* Background image layer - with scale transform for mobile */}
                                <div style={{
                                    position: 'absolute',
                                    inset: 0,
                                    ...previewStyle,
                                    ...getMobileTransform()
                                }} />
                                {/* Crosshair indicator */}
                                <div style={{
                                    position: 'absolute',
                                    left: `${currentSettings.positionX}%`,
                                    top: `${currentSettings.positionY}%`,
                                    transform: 'translate(-50%, -50%)',
                                    width: isMobile ? '24px' : '20px',
                                    height: isMobile ? '24px' : '20px',
                                    border: '2px solid white',
                                    borderRadius: '50%',
                                    boxShadow: '0 0 10px rgba(0,0,0,0.5)',
                                    pointerEvents: 'none',
                                    zIndex: 1
                                }} />
                            </div>
                        </div>
                    </div>

                    {/* Zoom Control */}
                    <div style={{
                        marginTop: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        justifyContent: 'center'
                    }}>
                        <ZoomIn size={20} color="rgba(255,255,255,0.6)" />
                        <input
                            type="range"
                            min="50"
                            max="200"
                            value={currentSettings.zoom}
                            onChange={(e) => handleZoomChange(Number(e.target.value))}
                            style={{
                                width: isMobile ? '150px' : '200px',
                                accentColor: '#a855f7'
                            }}
                        />
                        <span style={{ color: 'white', fontSize: '14px', minWidth: '50px' }}>
                            {currentSettings.zoom}%
                        </span>
                    </div>

                    {/* Position Values */}
                    <div style={{
                        marginTop: '12px',
                        display: 'flex',
                        gap: '16px',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.5)'
                    }}>
                        <span>X: {Math.round(currentSettings.positionX)}%</span>
                        <span>Y: {Math.round(currentSettings.positionY)}%</span>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: isMobile ? '16px' : '20px 24px',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: isMobile ? '10px 16px' : '12px 24px',
                            borderRadius: '12px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            background: 'transparent',
                            color: 'rgba(255,255,255,0.7)',
                            fontWeight: '600',
                            fontSize: isMobile ? '14px' : '16px',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleApply}
                        style={{
                            padding: isMobile ? '10px 16px' : '12px 24px',
                            borderRadius: '12px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: isMobile ? '14px' : '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Check size={18} />
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BackgroundPreviewModal;

